import { useState, memo } from 'react';
import { CELL_MIN_HEIGHT } from '../constants/layout';
import useTooltip from '../hooks/useTooltip';
import Tooltip from './Tooltip';
import './DayCell.scss';

/**
 * Renders a single day cell in the calendar grid.
 * Handles mouse interactions for selection, drag-and-drop, and click events.
 * Displays day number and optional holiday tooltip.
 * 
 * @param {Object} props
 * @param {Object} props.cell - Cell data from month.cells array
 * @param {string} props.cell.type - 'day' or 'empty'
 * @param {number} [props.cell.day] - Day of month (1-31)
 * @param {Date} [props.cell.date] - Full date object
 * @param {boolean} [props.cell.isWeekend] - Whether day is Saturday or Sunday
 * @param {boolean} [props.cell.isToday] - Whether day is today
 * @param {number} [props.height=CELL_MIN_HEIGHT] - Cell height in pixels
 * @param {boolean} [props.isSelected=false] - Whether day is in current selection range
 * @param {Object|null} [props.holiday=null] - Holiday data if day is a holiday
 * @param {Function} [props.onMouseDown] - Handler for mouse down (date, event) => void
 * @param {Function} [props.onMouseEnter] - Handler for mouse enter (date) => void
 * @param {Function} [props.onMouseUp] - Handler for mouse up () => void
 * @param {Function} [props.onClick] - Handler for click (date, event) => void
 * @param {Function} [props.onDragOver] - Handler for drag over (date, event) => void
 * @param {Function} [props.onDrop] - Handler for drop (date, event) => void
 */
const DayCell = memo(function DayCell({ 
  cell, 
  height = CELL_MIN_HEIGHT,
  isSelected = false,
  holiday = null,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
  onClick,
  onDragOver,
  onDrop,
  isDragging = false
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  // Use tooltip hook for holiday tooltips
  const { tooltipProps, handleMouseEnter: handleTooltipEnter, handleMouseLeave: handleTooltipLeave } = useTooltip({
    title: holiday?.title || '',
    description: holiday?.description,
    disabled: !holiday || isDragging
  });

  if (cell.type === 'empty') {
    return (
      <div 
        className={`day-cell day-cell--empty ${cell.dayOfWeek >= 5 ? 'day-cell--weekend' : ''}`}
        style={{ height: `${height}px` }}
      />
    );
  }

  const classNames = [
    'day-cell',
    cell.isWeekend ? 'day-cell--weekend' : '',
    cell.isToday ? 'day-cell--today' : '',
    isSelected ? 'day-cell--selected' : '',
    isDragOver ? 'day-cell--drag-over' : '',
    holiday ? 'day-cell--holiday' : ''
  ].filter(Boolean).join(' ');

  const handleMouseDown = (e) => {
    if (onMouseDown && cell.date) {
      onMouseDown(cell.date, e);
    }
  };

  const handleMouseEnter = (e) => {
    handleTooltipEnter(e);
    if (onMouseEnter && cell.date) {
      onMouseEnter(cell.date);
    }
  };

  const handleMouseLeave = () => {
    handleTooltipLeave();
  };

  const handleMouseUp = () => {
    if (onMouseUp) {
      onMouseUp();
    }
  };

  const handleClick = (e) => {
    if (onClick && cell.date) {
      onClick(cell.date, e);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
    if (onDragOver && cell.date) {
      onDragOver(cell.date, e);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (onDrop && cell.date) {
      onDrop(cell.date, e);
    }
  };

  // Format date as YYYY-MM-DD using local time (not UTC)
  const dateStr = cell.date 
    ? `${cell.date.getFullYear()}-${String(cell.date.getMonth() + 1).padStart(2, '0')}-${String(cell.date.getDate()).padStart(2, '0')}`
    : '';

  return (
    <div 
      className={classNames}
      style={{ height: `${height}px` }}
      data-date={dateStr}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className="day-cell__number">{cell.day}</span>
      {holiday && <Tooltip {...tooltipProps} />}
    </div>
  );
});

export default DayCell;
